import {ref, computed} from 'vue'
import {UserManager, WebStorageStateStore, Log, Logger} from 'oidc-client-ts'
import Cookies from 'js-cookie'
import * as Sentry from '@sentry/vue'
import {apolloClient} from 'shared/apollo'
import {onLogin, onLogout} from 'shared/apollo/vue-apollo'
import analytics from 'shared/utils/analytics'
import {api} from 'shared/plugins/api'
import {setIsEmailRequiredForBugReports} from 'shared/utils/birdSdk'
import messageExtension from 'shared/utils/messageExtension'
import sendLogoutToExtension from 'shared/utils/sendLogoutToExtension'

// logging level of oidc-client-ts library in development mode
if (import.meta.env.MODE !== 'production') {
	Log.setLogger(console)
	Log.setLevel(Log.DEBUG)
}

// create a logger for this plugin
const log = new Logger('AuthPlugin')

const loading = ref(true)
const authUser = ref(null)
const userManager = ref(null)
const isAuthenticated = ref(false)
const isExtension = import.meta.env.VUE_APP_TARGET === 'extension'

const user = computed(() => (authUser.value ? {...authUser.value, ...authUser.value.profile} : null))
const userId = computed(() => user.value?.sub)

const createUserManager = connection => {
	const redirectUri =
		import.meta.env.VUE_APP_TARGET === 'extension'
			? `${window.location.origin}/localSession.html`
			: `${window.location.origin}/auth/post-sso`

	const logoutRedirectUri =
		import.meta.env.VUE_APP_TARGET === 'extension'
			? `${window.location.origin}/localSession.html`
			: `${window.location.origin}/auth/logout`

	const baseConfig = {
		redirect_uri: redirectUri,
		post_logout_redirect_uri: 'https://birdeatsbug.com',
		response_type: 'code',
		userStore: new WebStorageStateStore({store: localStorage}),
	}

	log.info('UserManager created with baseConfig for connection', connection, baseConfig)

	if (connection === 'legacy') {
		return new UserManager({
			...baseConfig,
			authority: import.meta.env.VUE_APP_AUTH0_AUTHORITY,
			client_id: import.meta.env.VUE_APP_AUTH0_CLIENTID,
			scope: 'openid profile email offline_access',
			redirect_uri: redirectUri,
		})
	}

	return new UserManager({
		...baseConfig,
		authority: import.meta.env.VUE_APP_BROWSERSTACK_BASE_URL,
		client_id: import.meta.env.VUE_APP_BROWSERSTACK_CLIENTID,
		loadUserInfo: false,
		redirect_uri: redirectUri,
		metadata: {
			issuer: import.meta.env.VUE_APP_BROWSERSTACK_BASE_URL,
			authorization_endpoint: `${
				import.meta.env.VUE_APP_BROWSERSTACK_BASE_URL
			}/oauth2/authorize?product_type=bug_capture`,
			token_endpoint: `${import.meta.env.VUE_APP_API}/authentication/token`,
			end_session_endpoint: logoutRedirectUri,
		},
	})
}

const determineActiveUserManager = async connection => {
	// If an explicit connection is provided, use that
	if (connection) {
		return createUserManager(connection)
	}

	// HACK: If the query param contains code and state but not namespace,
	// then the request is coming from Auth0
	try {
		const params = new URLSearchParams(window.location.search)
		if (params.has('code') && params.has('state') && !params.has('namespace')) {
			return createUserManager('legacy')
		}
	} catch (error) {
		log.error('[determineActiveUserManager()] Error parsing query params:', error)
	}

	// If the beb-user cookie is set and the user is not logged in
	// use the legacy connection
	const bebUserCookieExists = !!Cookies.get('beb-user')

	// Determine which user manager to use based on the user's token
	const auth0UserManager = createUserManager('legacy')
	const browserstackUserManager = createUserManager('Browserstack')

	// Determine which user manager to use based on the user's token
	try {
		const auth0User = await auth0UserManager.getUser()
		const browserstackUser = await browserstackUserManager.getUser()

		if (auth0User?.id_token) {
			return auth0UserManager
		} else if (browserstackUser?.id_token) {
			return browserstackUserManager
		}
	} catch (error) {
		log.error('[determineActiveUserManager()] Error determining active user manager:', error)
	}

	return bebUserCookieExists ? auth0UserManager : browserstackUserManager
}

async function initialize(provider) {
	log.info('[initialize()] Initializing...')

	try {
		userManager.value = await determineActiveUserManager(provider)

		userManager.value.events.addUserLoaded(async user => {
			log.info('[events][UserLoaded]', {user})
			Sentry.getCurrentScope().setUser({id: user.profile.sub})
			analytics.identify(user.profile.sub)
			setIsEmailRequiredForBugReports(user.profile.email)
			onLogin(apolloClient)
		})

		userManager.value.events.addUserUnloaded(() => {
			log.info('[events][UserUnloaded]')
			Sentry.getCurrentScope().setUser({id: user.value?.profile?.sub})
			analytics.reset()
			setIsEmailRequiredForBugReports(null)
			onLogout(apolloClient)
		})

		authUser.value = await handleAuthentication()
		isAuthenticated.value = !!authUser.value
		if (
			!isAuthenticated.value ||
			userManager.value.settings.client_id === import.meta.env.VUE_APP_BROWSERSTACK_CLIENTID
		) {
			loading.value = false
			return
		}
		// Sync user with backend if part of legacy login
		await api.post(
			'/authentication/post-login',
			{},
			{
				headers: {
					'X-Bird-Trial':
						Boolean(authUser.value.state?.bugCaptureFreeTrial) ||
						import.meta.env.VUE_APP_TARGET === 'extension',
				},
			}
		)
		loading.value = false
	} catch (error) {
		Sentry.captureException(error)
		const managerProvider =
			userManager.value.settings.client_id === import.meta.env.VUE_APP_AUTH0_CLIENTID ? 'legacy' : 'Browserstack'
		log.error('[initialize()] Error during initialization for provider', {managerProvider})
		log.error(error)

		// in the case of signups via the extension, where the user has to click on a email confirmation link,
		// it is very likely that they will open a new window, which will loose the connection to the extension (therefore, "No window.opener")
		// In this case, the redirect won't know which window it should notify that the authentication happened successfully.
		// So we redirect the user to the app, so that they can complete signup, and warn any open LocalSession window of what happened.
		if (isExtension && error.message.includes('No window.opener')) {
			log.info('Signup/login process continued on a separate window, opening app to complete signup...')
			messageExtension('reauthenticate')
			location.href = `${import.meta.env.VUE_APP_URL}/auth/start-sso?bug-capture-free-trial=true`
			return
		}

		// if the error message contains "No matching state found in storage", it means the code in the URL was invalid
		// so we simply redirect to the login page for the login flow to start again
		if (error.message.includes('No matching state found in storage')) {
			Sentry.captureMessage(`Auth0 redirect handle callback failed: ${error.message}`)
			if (window.location.pathname !== '/auth/start-sso') {
				location.href = `${import.meta.env.VUE_APP_URL}/auth/start-sso`
			}
			return
		}

		// The /post-login route can return a 404 if the user is not found in Auth0
		// In this case, we want to logout the user and redirect to the login page
		if (error.status === 404 && error.message.includes('User not found in Auth0')) {
			await logout({silent: true})
			location.href = `${import.meta.env.VUE_APP_URL}/auth/start-sso`
			return
		}

		// Clear stale state and remove user from local storage and ask user to login again
		await userManager.value.clearStaleState()
		await userManager.value.removeUser()

		log.error('[initialize()] Authentication failed, redirecting to error page')
		location.href = `${import.meta.env.VUE_APP_URL}/auth/error?error=${encodeURIComponent(
			error.message
		)}&provider=${managerProvider}`
	}
}

async function handleAuthentication() {
	log.info('[handleAuthentication()]')

	const params = new URLSearchParams(window.location.search)

	// handle signin callback
	if (params.has('code')) {
		log.info('[handleAuthentication()] Code in url, signing in...')
		return userManager.value.signinCallback()
	}

	// handle signout callback, ensure it's not part of the Zapier OAuth request
	if (
		(params.has('state') && !params.has('code') && !params.has('client_id')) ||
		(params.has('id_token_hint') && params.has('post_logout_redirect_uri'))
	) {
		log.info('[handleAuthentication()] Logging out user...')
		return userManager.value.signoutCallback()
	}

	// otherwise, try to get the user from the local storage, then silently sign in if it exists
	log.info('[handleAuthentication()] Getting user from local storage...')
	const localStorageUser = await userManager.value.getUser()
	return localStorageUser ? userManager.value.signinSilent() : null
}

function reinitialize(provider) {
	log.info('[reinitialize()]', {provider})
	loading.value = true
	isAuthenticated.value = false
	user.value = null
	return initialize(provider)
}

// LOGGING IN

/** Log in user with fitting method for web app / extension
 *  - should be the only method used to log in the user
 * */
async function login({
	utmSourceOnLogin,
	redirectAfterLogin,
	provider = Cookies.get('beb-user') ? 'legacy' : 'Browserstack',
	bugCaptureFreeTrial = isExtension,
} = {}) {
	log.info('[login()]', {utmSourceOnLogin, redirectAfterLogin, provider, bugCaptureFreeTrial})

	// Include bugCaptureFreeTrial in the query params and token params both
	// to ensure it works with both legacy and Browserstack logins
	const options = {
		extraQueryParams: {
			provider,
		},
	}

	if (
		(provider === 'legacy' && userManager.value.settings.client_id !== import.meta.env.VUE_APP_AUTH0_CLIENTID) ||
		(provider === 'Browserstack' &&
			userManager.value.settings.client_id !== import.meta.env.VUE_APP_BROWSERSTACK_CLIENTID)
	) {
		userManager.value.clearStaleState()
		userManager.value.removeUser()
		await reinitialize(provider)
	}

	// EXTENSION LOGIN
	if (isExtension) {
		loading.value = true // It takes time to load the user after the popup is closed, so show a loader during this time
		try {
			await userManager.value.signinPopup({
				...options,
				popupWindowFeatures: {
					location: '_blank',
					toolbar: true,
					resizeable: true,
					scrollbars: true,
					status: true,
					popup: false,
					width: 1280,
					height: 960,
				},
			})
		} catch (error) {
			log.error('[login()] Error during extension login:', error)
			loading.value = false
			return
		}
		return reinitialize()
	}

	// APP LOGIN
	if (localStorage && utmSourceOnLogin) {
		localStorage.utmSourceOnLogin = utmSourceOnLogin
	}
	if (localStorage && redirectAfterLogin) {
		localStorage.redirectAfterLogin = redirectAfterLogin
	}

	return userManager.value.signinRedirect(options)
}

// LOGGING OUT

async function logout({logoutOfAllTargets = false, silent = false, force = false} = {}) {
	log.info('[logout()]', {logoutOfAllTargets, silent, force})

	if (!isAuthenticated.value) return

	if (silent) {
		await userManager.value.signoutSilent()
		return reinitialize()
	}

	// If we're forcing a logout, we want users to automatically get authenticated again
	// In case of manual logouts, we would redirect them to the homepage where they can opt to login again
	let postLogoutRedirectUri = isExtension ? `${window.location.origin}/localSession.html` : `https://birdeatsbug.com`
	if (force && !isExtension) {
		postLogoutRedirectUri = `${window.location.origin}/auth/start-sso`
	}

	if (isExtension) {
		if (logoutOfAllTargets) window.open(`${import.meta.env.VUE_APP_URL}/auth/logout?close=true`)
		await messageExtension('setUserInformation', {})
		await userManager.value.signoutPopup({
			id_token_hint: user.value.id_token,
			post_logout_redirect_uri: postLogoutRedirectUri,
			popupWindowFeatures: {
				location: '_blank',
				toolbar: true,
				resizeable: true,
				scrollbars: true,
				status: true,
				popup: false,
				width: 1280,
				height: 960,
			},
		})
		return reinitialize()
	}

	if (logoutOfAllTargets) {
		const extensionMetadata = await messageExtension('getExtensionMetadata')
		if (extensionMetadata?.environment === import.meta.env.MODE) {
			log.info('sending logout to extension...')
			await sendLogoutToExtension()
		}
	}

	log.info('Logging out from web app...')
	await fetch(import.meta.env.VUE_APP_API + '/authentication/cookie', {
		method: 'DELETE',
		credentials: 'include',
	})
	Cookies.remove('api-authentication-hash')
	return userManager.value.signoutRedirect({post_logout_redirect_uri: postLogoutRedirectUri})
}

async function getTokenSilently() {
	log.info('[getTokenSilently()]')
	const localStorageUser = await userManager.value.getUser()
	const currentTime = Math.floor(Date.now() / 1000)
	if (localStorageUser && currentTime < localStorageUser.expires_at) {
		return localStorageUser.access_token
	}
	// Sign in silently if the token has expired or user is not in local storage
	const user = await userManager.value.signinSilent()
	return user ? user.access_token : null
}

const authPlugin = {
	install: app => {
		// Initialize the plugin
		initialize()
			.then(() => {
				log.info('[initialize()]', 'Auth plugin initialized')
			})
			.catch(error => {
				log.error('[initialize()] Failed to initialize auth plugin:', error)
			})

		// Provide auth methods to components
		app.config.globalProperties.$auth = {
			loading,
			user,
			userId,
			isAuthenticated,
			login,
			logout,
			getTokenSilently,
		}
	},
	loading,
	user,
	userId,
	isAuthenticated,
	login,
	logout,
	getTokenSilently,
}

export default authPlugin
